import { html, LitElement } from "lit";
import { svgIcon } from "../../../src/utils/icon.js";

/** Supported purchase flows. `credits` is the default (recharge/points/gold). */
const PURCHASE_TYPE = { CREDITS: "credits", PREMIUM: "premium" };

/**
 * Shared header purchase CTA.
 *
 * A single element drives both the credits (recharge) and premium (subscription)
 * flows — the differences are data, not code:
 *
 *   • `purchase-type` ("credits" default | "premium") selects which paywall we
 *     track and, when no URL is given, which config field supplies it.
 *   • `purchase-url` (optional) is an explicit override that always wins. Without
 *     it we resolve by type — credits reads the WebLive config
 *     (economy.purchase.url) with the server-injected `href` as fallback; premium
 *     has no config field, so it uses `href` (env/brand default). This is the
 *     same config > env priority the server applies when injecting `href`.
 *   • `include-token` decides how the URL opens: on → append a fresh TMG
 *     access_token (`${url}?access_token=…`); off → open the URL directly.
 *
 * Brand deviation (mirrors the web-app): lovoo is NOT on the TMG economy backend,
 * so its buttons ship with `include-token` off and open the old POS deep-link
 * with `?is-only-payment=1`. Credits additionally listens for the
 * `payment-success` postMessage to close the tab and refresh balances; premium is
 * fire-and-forget. Every other brand sets `include-token` on and opens
 * `${url}?access_token=…`.
 */
export class PurchaseButton extends LitElement {
  static properties = {
    href: { type: String },
    icon: { type: String },
    text: { type: String },
    classes: { type: String },
    // Active brand (e.g. "lovoo"), passed in from the server-rendered template.
    brand: { type: String },
    // The TMG access_token, already exchanged server-side and rendered in.
    token: { type: String },
    // Absolute expiry (ms epoch) of `token`, so the client can tell when the
    // server-injected snapshot has gone stale and must be renewed before use.
    tokenExpiresAt: { type: Number, attribute: "token-expires-at" },
    // "credits" (default) | "premium" — selects the flow behaviour.
    purchaseType: { type: String, attribute: "purchase-type" },
    // Explicit URL override; when absent we resolve by type (config > env).
    purchaseUrl: { type: String, attribute: "purchase-url" },
    // Append the TMG access_token to the URL (TMG token flow). Off for the
    // lovoo POS-session flow, which opens the deep-link with is-only-payment.
    includeToken: { type: Boolean, attribute: "include-token" },
    // Extra classes for the icon span (e.g. "mobile-hidden" to show the icon on
    // desktop but render text-only on mobile).
    iconClasses: { type: String, attribute: "icon-classes" },
  };

  constructor() {
    super();
    this.href = "";
    this.icon = "";
    this.text = "";
    this.classes = "";
    this.brand = "";
    this.token = "";
    this.tokenExpiresAt = 0;
    this.purchaseType = PURCHASE_TYPE.CREDITS;
    this.purchaseUrl = "";
    this.includeToken = false;
    this.iconClasses = "";
  }

  createRenderRoot() {
    return this;
  }

  get _isPremium() {
    return this.purchaseType === PURCHASE_TYPE.PREMIUM;
  }

  get _isLovoo() {
    return this.brand === "lovoo";
  }

  _isTokenFresh() {
    return Boolean(this.token) && Date.now() < Number(this.tokenExpiresAt);
  }

  /**
   * Resolve the URL to open.
   *
   * Explicit `purchase-url` wins. Otherwise credits prefers the client-loaded
   * WebLive config (economy.purchase.url) and falls back to the server-injected
   * `href`; premium has no config field, so it uses `href` directly (env/brand).
   */
  _resolveUrl() {
    if (this.purchaseUrl) return this.purchaseUrl;
    if (this._isPremium) return this.href;
    return window.tmgConfig?.economy?.purchase?.url ?? this.href;
  }

  async _fetchFreshToken() {
    // The token is a generic, brand-scoped TMG access_token (not credits
    // specific), so premium reuses the same exchange endpoint.
    const res = await fetch("/r/api/credits-token", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`credits-token ${res.status}`);
    const data = await res.json();
    if (!data?.accessToken) throw new Error("credits-token: empty token");
    // Cache locally so later clicks in this pageview reuse it while it's valid.
    this.token = data.accessToken;
    if (data.expiresAt) this.tokenExpiresAt = Number(data.expiresAt);
    return data.accessToken;
  }

  _navigate(popup, url) {
    if (popup) popup.location = url;
    else window.open(url, "_blank");
  }

  _originOf(url) {
    try {
      return new URL(url).origin;
    } catch {
      return url;
    }
  }

  /**
   * No-token path. lovoo opens the old POS deep-link with `?is-only-payment=1`
   * (credits also waits for the `payment-success` postMessage to close the tab
   * and refresh the balance; premium is fire-and-forget). Any other brand
   * without a token simply opens the URL.
   */
  _openDirect(url) {
    if (!this._isLovoo) {
      window.open(url, "_blank");
      return;
    }

    const popup = window.open(`${url}?is-only-payment=1`, "_blank");

    if (this._isPremium) return;

    const origin = this._originOf(url);
    const onMessage = (event) => {
      if (event.origin === origin && event.data === "payment-success") {
        window.removeEventListener("message", onMessage);
        popup?.close();
        window.location.reload();
      }
    };
    window.addEventListener("message", onMessage);
  }

  /**
   * Token flow: open `${url}?access_token=…` with a fresh token.
   *
   * Fast path uses the injected token synchronously (no request, no popup-blocker
   * risk). When it has expired we open a blank tab inside the click gesture, renew
   * the token, then point the tab at the real URL.
   */
  async _openWithToken(url) {
    if (this._isTokenFresh()) {
      window.open(`${url}?access_token=${this.token}`, "_blank");
      return;
    }

    const popup = window.open("", "_blank");
    try {
      const token = await this._fetchFreshToken();
      this._navigate(popup, `${url}?access_token=${token}`);
    } catch {
      // Renewal failed: try the (possibly stale) injected token over nothing.
      this._navigate(popup, `${url}?access_token=${this.token}`);
    }
  }

  _handleClick(e) {
    e.preventDefault();

    const ctx = {
      paywall_name: this.purchaseType,
      context_entry_point: "navigation",
      context_reason: "organic",
    };

    this.dispatchEvent(
      new CustomEvent("purchase", {
        bubbles: true,
        composed: true,
        detail: { step: "pos_opened", ...ctx },
      }),
    );

    if (typeof window.trackEvent === "function") {
      window.trackEvent("pos_event", { step: "pos_opened", ...ctx });
    }

    const url = this._resolveUrl();
    if (this.includeToken) this._openWithToken(url);
    else this._openDirect(url);
  }

  render() {
    const href = this.purchaseUrl || this.href;
    return html`
      <a
        href="${href}"
        class="button ${this.classes}"
        target="_blank"
        @click="${this._handleClick}"
      >
        ${this.icon ? html`<span class="button-icon icon${this.iconClasses ? ` ${this.iconClasses}` : ""}">${svgIcon(this.icon)}</span>` : ""}
        ${this.text ? html`<span class="button-text">${this.text}</span>` : ""}
      </a>
    `;
  }
}

customElements.define("purchase-button", PurchaseButton);
